import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Plus } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { EMPLOYEE_ROLES } from "../../../api/creditAccounts";
import { createStakeholders } from "../../stakeholder/logic/stakeholderThunks";
import { getStakeholders } from "../../stakeholder/logic/stakeholderSlice";
import ManagementModal from "../../../../src/components/ui/modals/ManagementModal";
import { EntityType, RelatedAs } from "../../../../src/api/stakeholder_relationships";
import ButtonV2 from "../../../components/ui/button/v2";
import PartyRelationRow, {
  buildEmptyRelationItem,
  getPartyRelationshipErrors,
  PartyRelationship,
} from "./PartyRelationRow";
import EmployeeModal, { EmployeePartyItem } from "./EmployeeModal";

export const isPartyRelationshipValid = (
  party: PartyRelationship,
  all: PartyRelationship[],
  existingStakeholders?: Array<{ name: string }>
) => {
  const { name, relations, account_numbers } = getPartyRelationshipErrors(party, all, existingStakeholders);
  return (
    !name &&
    Object.keys(relations).length === 0 &&
    Object.keys(account_numbers).length === 0 &&
    party.relations.length > 0
  );
};

interface PartyModalProps {
  onClose: () => void;
  onBack?: () => void;
}

const buildInitialParty = (): PartyRelationship => ({
  id: uuidv4(),
  name: "",
  relations: [buildEmptyRelationItem()],
});

const PartyModal: React.FC<PartyModalProps> = ({ onClose, onBack }) => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();

  const existingStakeholders = useAppSelector(getStakeholders);

  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const [partyRelationships, setPartyRelationships] = useState<PartyRelationship[]>([
    buildInitialParty(),
  ]);

  const employeeParties: EmployeePartyItem[] = useMemo(() => {
    return partyRelationships
      .filter((party) =>
        party.relations.some(
          (r) => r.related_as !== "" && EMPLOYEE_ROLES.includes(r.related_as as RelatedAs)
        )
      )
      .map((party) => ({
        partyId: party.id,
        name: party.name,
        types: party.relations
          .map((r) => r.related_as)
          .filter((r): r is RelatedAs => r !== "" && EMPLOYEE_ROLES.includes(r)),
      }));
  }, [partyRelationships]);

  const hasEmployeeTypes = employeeParties.length > 0;

  const areAllPartiesValid =
    partyRelationships.length > 0 &&
    partyRelationships.every((party) =>
      isPartyRelationshipValid(party, partyRelationships, existingStakeholders)
    );

  const handleAddParty = () => {
    if (!areAllPartiesValid) {
      setShowErrors(true);
      return;
    }
    setPartyRelationships((prev) => [...prev, buildInitialParty()]);
  };

  const handleDeleteParty = (partyId: string) => {
    setPartyRelationships((prev) => prev.filter((p) => p.id !== partyId));
  };

  const handleChangeParty = (updated: PartyRelationship) => {
    setPartyRelationships((prev) =>
      prev.map((p) => (p.id === updated.id ? updated : p))
    );
  };

  const handleSubmit = () => {
    setShowErrors(true);

    if (!areAllPartiesValid) {
      return;
    }

    if (hasEmployeeTypes) {
      setEmployeeModalOpen(true);
      return;
    }

    handleCreateStakeholders();
  };

  const handleCreateStakeholders = async (empMap: Record<string, string> = {}) => {
    if (!caseId || !areAllPartiesValid) {
      return;
    }

    setIsSaving(true);

    const stakeholders = partyRelationships.map((party) => {
      const relationships = party.relations.flatMap((relation) =>
        relation.account_numbers.map((loanAccountId) => ({
          entity_type: EntityType.LOAN_ACCOUNT,
          entity_id: loanAccountId,
          related_as: relation.related_as as RelatedAs,
        }))
      );

      const empId = empMap[party.id];

      return {
        stakeholder: {
          name: party.name.trim(),
          is_potential_perpetrator: true,
        },
        relationships: relationships.length > 0 ? relationships : undefined,
        ...(empId && {
          employee_details: {
            employee_id: empId,
          },
        }),
      };
    });

    try {
      await dispatch(
        createStakeholders({
          caseId,
          request: {
            stakeholders,
          },
        })
      ).unwrap();

      setEmployeeModalOpen(false);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (employeeModalOpen) {
    return (
      <EmployeeModal
        employees={employeeParties}
        onClose={onClose}
        onSave={handleCreateStakeholders}
        onBack={() => setEmployeeModalOpen(false)}
      />
    );
  }

  return (
    <ManagementModal
      title="Add Party"
      onClose={onClose}
      onCancel={onClose}
      onBack={onBack}
      onSubmit={handleSubmit}
      submitLabel={hasEmployeeTypes ? "Provide Employee Details" : "Save"}
      cancelLabel="Discard"
      submitEnabled={!isSaving}
      isLoading={isSaving}
      className="w-[932px] max-w-[80vw]"
    >
      <div className="flex flex-col gap-6">
        {partyRelationships.map((party) => (
          <PartyRelationRow
            key={party.id}
            partyRelationship={party}
            allParties={partyRelationships}
            showErrors={showErrors}
            onChange={handleChangeParty}
            onDelete={
              partyRelationships.length > 1
                ? () => handleDeleteParty(party.id)
                : undefined
            }
          />
        ))}

        <ButtonV2
          variant="ghost"
          size="sm"
          disabled={!areAllPartiesValid}
          onClick={handleAddParty}
          className="text-[#3551F3] bg-transparent self-start disabled:opacity-40 hover:bg-transparent hover:text-[#304ADD] px-0 h-auto"
          prefix={<Plus width="16" height="16" />}
        >
          Add Primary Party
        </ButtonV2>
      </div>
    </ManagementModal>
  );
};

export default PartyModal;